import { type ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import classNames from 'classnames'
import * as R from 'ramda'
import { formatDate } from '@pbt/pbt-ui-components/src/utils/dateUtils'

import { ConversationStatus } from '~/constants/communications'
import { fetchConversationMessagesList } from '~/store/actions/conversations'
import { getChatMessages } from '~/store/reducers/conversationMessages'
import { getConversationMessagesLoading } from '~/store/reducers/conversations'
import type { Conversation } from '~/types'
import { isPlainTextTransport } from '~/utils/communicationsUtils'

import { communicationsSnapshotColumns } from './communicationsSnapshotColumns'
import MessageDateCell from './MessageDateCell'
import MessageRow from './MessageRow'
import RowSkeleton from './RowSkeleton'
import SubjectCell from './SubjectCell'

import styles from './ConversationRow.module.scss'

const ConversationRow = ({ conversation }: { conversation: Conversation }) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const messages = useSelector(getChatMessages(conversation?.messages || []))
  const areMessagesLoading = useSelector(getConversationMessagesLoading)

  const appointmentName =
    R.prop('name', conversation.event?.businessAppointmentType) || ''
  const appointmentDate = R.prop('scheduledStartDatetime', conversation.event)

  const { id, title, status, isDraft, lastMessage, messagesCount, transport } =
    conversation

  const isExpandable = (messagesCount ?? 0) > 1
  const isPlainText = isPlainTextTransport(transport)
  const isUnread = status === ConversationStatus.UNREAD

  useEffect(() => {
    // Only fetch messages when the row is expanded
    if (open && id) {
      dispatch(fetchConversationMessagesList(0, 4, id))
    }
  }, [open, id])

  useEffect(() => {
    if (R.isNil(lastMessage)) {
      dispatch(fetchConversationMessagesList(0, 0, id))
    }
  }, [lastMessage])

  const messageContent = lastMessage?.body ?? messages[0]?.body ?? ''

  return (
    <>
      <TableRow
        className={classNames(styles.tableRow, {
          [styles.unreadTableRow]: isUnread,
        })}
        data-testid="conversation-row"
      >
        <TableCell
          className={classNames(styles.expandCell, {
            [styles.noBorder]: open,
          })}
        >
          {isExpandable && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          )}
        </TableCell>
        <MessageDateCell
          lastMessageDate={conversation.lastMessageDate}
          noBottomBorder={open}
        />
        <SubjectCell
          conversationId={id}
          isDraft={isDraft && Boolean(messagesCount)}
          isPlainText={isPlainText}
          isUnread={isUnread}
          message={messageContent}
          noBottomBorder={open}
          subject={title}
        />
        <TableCell
          className={classNames(styles.tableCell, {
            [styles.noBorder]: open,
          })}
        >
          {appointmentName || appointmentDate
            ? `${appointmentName} ${formatDate(appointmentDate)}`
            : '—'}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={classNames(styles.messageRow)} colSpan={4}>
          <Collapse
            unmountOnExit
            className={classNames({
              [styles.expandedRow]: open,
            })}
            in={open}
          >
            <Table size="small" style={{ tableLayout: 'fixed' }}>
              <TableBody>
                {areMessagesLoading &&
                Boolean(messagesCount) &&
                R.isEmpty(messages) ? (
                  <RowSkeleton
                    columns={communicationsSnapshotColumns}
                    rows={R.repeat({}, 5)}
                  />
                ) : (
                  messages.map((message) => (
                    <MessageRow
                      conversation={conversation}
                      key={message.id}
                      message={message}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default ConversationRow
